import type {
  CellContext,
  ColumnDef,
  ColumnDefTemplate,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import type {
  SimpleTableColumnDef,
  SimpleTableDisplayColumnDef,
  SimpleTableGroupColumnDef,
  SimpleTableCustomCell,
  SimpleTableRowData,
} from '../public.api.js';

/**
 * @internal
 * Type guard to determine if a given column definition is a `SimpleTableGroupColumnDef`.
 */
export function isSimpleTableGroupColumnDef<
  TData extends SimpleTableRowData,
  TValue = unknown,
>(columnDef: unknown): columnDef is SimpleTableGroupColumnDef<TData, TValue> {
  return (
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'columns' in columnDef &&
    Array.isArray((columnDef as { columns: Array<unknown> }).columns)
  );
}

/**
 * @internal
 * Type guard to determine if a given column definition is a `SimpleTableDisplayColumnDef`.
 */
function isSimpleTableDisplayColumnDef<
  TData extends SimpleTableRowData,
  TValue = unknown,
>(columnDef: unknown): columnDef is SimpleTableDisplayColumnDef<TData, TValue> {
  return (
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'accessor' in columnDef
  );
}

/**
 * @internal
 * Helper function to transform the public accessor key to the correct
 * member config for the tanstack/react-table ColumnDef.
 */
function splitAccessor<TData extends SimpleTableRowData, TValue = unknown>(
  column: SimpleTableDisplayColumnDef<TData, TValue>,
): {
  accessorKey?: string;
  accessorFn?: (row: TData) => TValue;
} {
  if (typeof column.accessor === 'string') {
    return {
      accessorKey: column.accessor,
    };
  }

  if (typeof column.accessor === 'function') {
    return {
      accessorFn: column.accessor,
    };
  }
  return {};
}

/**
 * Maps the given header renderer to the internal header renderer and prevents
 * leaking.
 * @internal
 */
function createHeaderRenderer<TData extends SimpleTableRowData>(
  header: SimpleTableColumnDef<TData>['header'],
) {
  // We need to map the function from the outside here in order to not leak
  // tanstack table internals.
  if (typeof header === 'function') {
    return () => header();
  }
  return header;
}

/**
 * @internal
 * Helper function to convert from the public `SimpleTableDisplayColumnDef` to the internally used
 * ColumnDef.
 */
function createDisplayColumnDef<
  TData extends SimpleTableRowData,
  TValue = unknown,
>(
  columnDef: SimpleTableDisplayColumnDef<TData, TValue>,
): ColumnDef<TData, TValue> {
  const composedDef: ColumnDef<TData, TValue> = {
    id: columnDef.id,
    header: createHeaderRenderer(columnDef.header),
    meta: {
      width: columnDef.width,
      minWidth: columnDef.minWidth,
      maxWidth: columnDef.maxWidth,
      alignment: columnDef.alignment ?? 'left',
    },
    ...splitAccessor(columnDef),
  };

  if (columnDef.cell) {
    const customCell = columnDef.cell;

    if (typeof customCell === 'string') {
      composedDef.cell = customCell;
      return composedDef;
    }

    if (typeof customCell === 'function') {
      return {
        ...composedDef,
        cell: createCellRenderer(customCell),
      };
    }

    return composedDef;
  }

  return composedDef;
}

/**
 * Prepares all exposed functions for the custom cell renderer.
 */
function createCellRenderer<TData extends SimpleTableRowData, TValue>(
  consumerCustomCell: SimpleTableCustomCell<TData, TValue>,
): ColumnDefTemplate<CellContext<TData, TValue>> {
  return ({ getValue, row, column }) => {
    return consumerCustomCell({
      value: getValue(),
      rowIndex: row.index,
      rowData: row.original,
      rowId: row.id,
    });
  };
}

/**
 * Helper hook that will calculate which columns are enabled within the SimpleTable
 * @internal
 */
export function useColumns<TData extends SimpleTableRowData, TValue = unknown>(
  propColumns: SimpleTableColumnDef<TData, TValue>[],
) {
  return useMemo(() => {
    const createdColumns: ColumnDef<TData, TValue>[] = [];

    // Iterate through the columns and create the definitions required
    for (const firstLevelColumn of propColumns) {
      if (isSimpleTableGroupColumnDef(firstLevelColumn)) {
        const groupColumn: ColumnDef<TData, TValue> = {
          id: firstLevelColumn.id,
          header: createHeaderRenderer(firstLevelColumn.header),
          columns: firstLevelColumn.columns?.map((childColumn) =>
            createDisplayColumnDef<TData>(childColumn),
          ),
          meta: {
            width: firstLevelColumn.width,
            alignment: firstLevelColumn.alignment ?? 'left',
          },
        };
        createdColumns.push(groupColumn);
      } else if (isSimpleTableDisplayColumnDef(firstLevelColumn)) {
        createdColumns.push(createDisplayColumnDef<TData>(firstLevelColumn));
      }
    }

    return createdColumns;
  }, [propColumns]);
}
